// src/components/admin/employees/wizard/JobDetails.js
import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useEmployees } from '../../../../contexts/EmployeeContext';
import functionalStructureService from '../../../../services/functionalStructure.service';

const JobDetails = ({ formData, updateFormData, theme, departments }) => {
    const { employees } = useEmployees();
    const [legalEntities, setLegalEntities] = useState([]);
    const [showAddLegalEntity, setShowAddLegalEntity] = useState(false);
    const [newLegalEntityName, setNewLegalEntityName] = useState('');
    const [creatingLegalEntity, setCreatingLegalEntity] = useState(false);
    const [legalEntityError, setLegalEntityError] = useState('');
    const [showAddDepartment, setShowAddDepartment] = useState(false);
    const [newDepartmentName, setNewDepartmentName] = useState('');
    const [departmentError, setDepartmentError] = useState('');
    const [departmentList, setDepartmentList] = useState([]);
    const [managerSearch, setManagerSearch] = useState('');
    const [showManagerDropdown, setShowManagerDropdown] = useState(false);

    // Load legal entities from Functional Structure
    const loadLegalEntities = async () => {
        try {
            const data = await functionalStructureService.getActive();
            if (data && data.structure_data) {
                const entities = data.structure_data.legalEntities || [];
                const validEntities = entities.filter(e => !e.isNew && e.name && e.name !== 'Add Legal Entity');
                setLegalEntities(validEntities);
            } else {
                setLegalEntities([]);
            }
        } catch (error) {
            console.error('Error loading functional structure:', error);
            setLegalEntities([]);
        }
    };

    useEffect(() => {
        loadLegalEntities();
    }, []);

    // Close manager dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showManagerDropdown && !e.target.closest('.relative')) {
                setShowManagerDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showManagerDropdown]);

    // Load departments for selected legal entity
    useEffect(() => {
        //console.log('Legal entity changed in formData:', formData.legalEntity);
        //console.log('Available legal entities:', legalEntities);
        if (formData.legalEntity) {
            // If legal entity is selected, show only its departments
            const selectedEntity = legalEntities.find(e => e.name === formData.legalEntity);
            //console.log('Selected entity:', selectedEntity);
            if (selectedEntity) {
                //console.log('Setting departments:', selectedEntity.departments);
                setDepartmentList(selectedEntity.departments || []);
            }
        } else if (legalEntities.length > 0) {
            // If no legal entity selected, show ALL departments from ALL legal entities
            //console.log('No legal entity selected, loading ALL departments');
            const allDepartments = legalEntities.flatMap(entity => entity.departments || []);
            // Remove duplicates by department name
            const uniqueDepartments = Array.from(
                new Map(allDepartments.map(dept => [dept.name, dept])).values()
            );
            //console.log('All unique departments:', uniqueDepartments);
            setDepartmentList(uniqueDepartments);
        } else {
            //console.log('No legal entities available, clearing departments');
            setDepartmentList([]);
        }
    }, [formData.legalEntity, legalEntities]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        //console.log('Field changed:', name, 'New value:', value);

        if (name === 'legalEntity' && value === '__add_legal_entity__') {
            setShowAddLegalEntity(true);
            return;
        }

        if (name === 'department' && value === '__add_department__') {
            if (!formData.legalEntity) {
                setDepartmentError('Please select legal entity first');
                return;
            }
            setDepartmentError('');
            setShowAddDepartment(true);
            return;
        }

        // If legal entity changes, update both legal entity and clear department in one call
        if (name === 'legalEntity') {
            //console.log('Legal entity changed, clearing department');
            setDepartmentError('');
            updateFormData({ [name]: value, department: '' });
        } else {
            updateFormData({ [name]: value });
        }
    };

    const handleAddDepartment = async () => {
        if (!formData.legalEntity) {
            setDepartmentError('Please select legal entity first');
            return;
        }

        if (newDepartmentName.trim() && formData.legalEntity) {
            try {
                setDepartmentError('');
                const data = await functionalStructureService.getActive();
                if (data && data.structure_data) {
                    const parsed = data.structure_data;
                    const entityIndex = parsed.legalEntities.findIndex(e => e.name === formData.legalEntity);

                    if (entityIndex !== -1) {
                        const newDept = {
                            id: `dept-${Date.now()}`,
                            name: newDepartmentName.trim(),
                            employees: 0,
                            isNew: false
                        };

                        parsed.legalEntities[entityIndex].departments.push(newDept);

                        // Save to backend
                        await functionalStructureService.upsert(parsed);

                        // Update local state
                        setLegalEntities(parsed.legalEntities);
                        setDepartmentList(parsed.legalEntities[entityIndex].departments);
                        updateFormData({ department: newDepartmentName.trim() });
                        setNewDepartmentName('');
                        setShowAddDepartment(false);
                        setDepartmentError('');
                    }
                }
            } catch (e) {
                console.error('Error adding department:', e);
            }
        }
    };

    const handleAddLegalEntity = async () => {
        const legalEntityName = newLegalEntityName.trim();
        if (!legalEntityName) {
            setLegalEntityError('Enter a legal entity name');
            return;
        }

        try {
            setCreatingLegalEntity(true);
            setLegalEntityError('');

            const data = await functionalStructureService.getActive();
            const currentStructure = data?.structure_data || {};
            const currentLegalEntities = Array.isArray(currentStructure.legalEntities)
                ? currentStructure.legalEntities
                : [];

            const duplicate = currentLegalEntities.some(
                (entity) => entity?.name?.toLowerCase() === legalEntityName.toLowerCase()
            );
            if (duplicate) {
                setLegalEntityError('A legal entity with this name already exists');
                return;
            }

            const newEntity = {
                id: `le-${Date.now()}`,
                name: legalEntityName,
                departments: [],
                positions: [],
                employees: 0,
                expanded: true,
                isNew: false
            };

            const updatedStructure = {
                ...currentStructure,
                legalEntities: [...currentLegalEntities, newEntity]
            };

            await functionalStructureService.upsert(updatedStructure);
            await loadLegalEntities();

            updateFormData({ legalEntity: legalEntityName, department: '' });
            setNewLegalEntityName('');
            setShowAddLegalEntity(false);
        } catch (error) {
            console.error('Error adding legal entity:', error);
            setLegalEntityError('Unable to create legal entity. Please try again.');
        } finally {
            setCreatingLegalEntity(false);
        }
    };

    const inputClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
        theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
    }`;

    const selectClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
        theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
    }`;

    const labelClass = `block text-sm font-medium mb-1.5 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
    }`;

    const sectionTitleClass = `text-base font-semibold mb-3 ${
        theme === 'dark' ? 'text-white' : 'text-gray-900'
    }`;

    return (
        <div className="space-y-4">
            <h3 className={sectionTitleClass}>Employment Details</h3>

            {/* Joining Date and Job Title */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>
                        Joining Date<span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        name="joiningDate"
                        value={formData.joiningDate}
                        onChange={handleChange}
                        required
                        className={inputClass}
                    />
                </div>
                <div>
                    <label className={labelClass}>
                        Location<span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Enter Location"
                        required
                        className={inputClass}
                    />
                </div>

            </div>

            {/* Time Type and Legal Entity */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>
                        Legal Entity<span className="text-red-500">*</span>
                    </label>
                    {!showAddLegalEntity ? (
                        <>
                            <div className="relative">
                                <select
                                    name="legalEntity"
                                    value={formData.legalEntity}
                                    onChange={handleChange}
                                    required
                                    className={selectClass}
                                >
                                    <option value="">Select Legal Entity</option>
                                    {legalEntities.length === 0 && (
                                        <option value="" disabled>No legal entities found</option>
                                    )}
                                    <option value="__add_legal_entity__">+ Add Legal Entity</option>
                                    {legalEntities.map((entity) => (
                                        <option key={entity.id} value={entity.name}>
                                            {entity.name}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={() => setShowAddLegalEntity(true)}
                                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}
                                    title="Add Legal Entity"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            {legalEntities.length === 0 && (
                                <p className="text-xs text-orange-500 mt-1">
                                    No legal entities found. Create one here to continue.
                                </p>
                            )}
                        </>
                    ) : (
                        <div className="space-y-2">
                            <input
                                type="text"
                                value={newLegalEntityName}
                                onChange={(e) => {
                                    setNewLegalEntityName(e.target.value);
                                    if (legalEntityError) setLegalEntityError('');
                                }}
                                placeholder="Enter legal entity name (e.g., OakHR India Pvt Ltd)"
                                className={inputClass}
                                autoFocus
                            />
                            {legalEntityError && (
                                <p className="text-xs text-red-500">{legalEntityError}</p>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleAddLegalEntity}
                                    disabled={creatingLegalEntity}
                                    className={`px-3 py-2 text-sm rounded-md ${
                                        creatingLegalEntity
                                            ? 'bg-gray-400 text-white cursor-not-allowed'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                                >
                                    {creatingLegalEntity ? 'Adding...' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddLegalEntity(false);
                                        setNewLegalEntityName('');
                                        setLegalEntityError('');
                                    }}
                                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <div>
                    <label className={labelClass}>
                        Department<span className="text-red-500">*</span>
                    </label>
                    {!showAddDepartment ? (
                        <div className="relative">
                            <select
                                name="department"
                                value={formData.department}
                                onChange={handleChange}
                                required
                                className={selectClass}
                            >
                                <option value="">
                                    {formData.legalEntity
                                        ? 'Select Department'
                                        : 'Select Department (All Legal Entities)'}
                                </option>
                                <option value="__add_department__">+ Add Department</option>
                                {departmentList.map((dept) => (
                                    <option key={dept.id} value={dept.name}>
                                        {dept.name}
                                    </option>
                                ))}
                            </select>
                            {formData.legalEntity && (
                                <button
                                    type="button"
                                    onClick={() => setShowAddDepartment(true)}
                                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}
                                    title="Add Department"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            )}
                            {departmentError && (
                                <p className="text-xs text-red-500 mt-1">{departmentError}</p>
                            )}
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newDepartmentName}
                                onChange={(e) => {
                                    setNewDepartmentName(e.target.value);
                                    if (departmentError) setDepartmentError('');
                                }}
                                placeholder="Enter Department Name"
                                className={inputClass}
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={handleAddDepartment}
                                className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                            >
                                Add
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowAddDepartment(false);
                                    setNewDepartmentName('');
                                    setDepartmentError('');
                                }}
                                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>

            </div>

            {/* Department and Location */}
            <div className="grid grid-cols-2 gap-3">

                <div>
                    <label className={labelClass}>
                        Job Title<span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        placeholder="Add Job Title"
                        required
                        className={inputClass}
                    />
                </div>
                <div className="relative">
                    <label className={labelClass}>Reporting Manager</label>
                    <input
                        type="text"
                        value={formData.reportingManager || managerSearch}
                        onChange={(e) => {
                            setManagerSearch(e.target.value);
                            setShowManagerDropdown(true);
                            if (!e.target.value) {
                                updateFormData({ reportingManager: '' });
                            }
                        }}
                        onFocus={() => setShowManagerDropdown(true)}
                        placeholder="Search for manager..."
                        className={inputClass}
                    />
                    {showManagerDropdown && (
                        <div className={`absolute z-50 w-full mt-1 max-h-60 overflow-auto border rounded-md shadow-lg ${
                            theme === 'dark' ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'
                        }`}>
                            {employees.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                    No employees found
                                </div>
                            ) : (
                                employees
                                    .filter(emp => {
                                        const searchTerm = (formData.reportingManager || managerSearch).toLowerCase();
                                        return emp.name?.toLowerCase().includes(searchTerm) ||
                                               emp.employee_code?.toLowerCase().includes(searchTerm) ||
                                               (emp.designation || emp.position || '')?.toLowerCase().includes(searchTerm);
                                    })
                                    .map((emp) => (
                                        <div
                                            key={emp.id}
                                            onClick={() => {
                                                updateFormData({ reportingManager: emp.name });
                                                setManagerSearch('');
                                                setShowManagerDropdown(false);
                                            }}
                                            className={`px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}
                                        >
                                            <div className="font-medium">{emp.name}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {emp.employee_code} • {emp.designation || emp.position || 'Employee'}
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>
                    )}
                </div>

            </div>

            {/* Worker Type and Reporting Manager */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>
                        Worker Type<span className="text-red-500">*</span>
                    </label>
                    <select
                        name="workerType"
                        value={formData.workerType}
                        onChange={handleChange}
                        required
                        className={selectClass}
                        size="1"
                    >
                        <option value="Permanent">Permanent</option>
                        <option value="Contract">Contract</option>
                        <option value="Temporary">Temporary</option>
                        <option value="Intern">Intern</option>
                    </select>
                </div>
                <div>
                    <label className={labelClass}>
                        Time Type<span className="text-red-500">*</span>
                    </label>
                    <select
                        name="timeType"
                        value={formData.timeType}
                        onChange={handleChange}
                        required
                        className={selectClass}
                        size="1"
                    >
                        <option value="Full Time">Full Time</option>
                        <option value="Part Time">Part Time</option>
                        <option value="Contract">Contract</option>
                    </select>
                </div>

            </div>

            {/* Probation Policy and Notice Period */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>Probation Policy</label>
                    <select
                        name="probationPolicy"
                        value={formData.probationPolicy}
                        onChange={handleChange}
                        className={selectClass}
                        size="1"
                    >
                        <option value="Default Probation Policy">Default Probation Policy</option>
                        <option value="3 Months">3 Months</option>
                        <option value="6 Months">6 Months</option>
                        <option value="No Probation">No Probation</option>
                    </select>
                </div>
                <div>
                    <label className={labelClass}>Notice Period</label>
                    <select
                        name="noticePeriod"
                        value={formData.noticePeriod}
                        onChange={handleChange}
                        className={selectClass}
                        size="1"
                    >
                        <option value="Default Notice Period">Default Notice Period</option>
                        <option value="1 Month">1 Month</option>
                        <option value="2 Months">2 Months</option>
                        <option value="3 Months">3 Months</option>
                    </select>
                </div>
            </div>
        </div>
    );
};

export default JobDetails;
